import { SuitesType } from '@api';
import { FormActions } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { NumberField, TextareaField } from '@hypeit-gmbh/hypeit-ui';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  SuitesFormDataType,
  useUpdateVenueSuites,
  useVenueSuitesFormSchema,
} from './hooks';

const TRANS_PATH = 'VENUE_SUITES.FORM';

type VenueSuitesFormPropsType = {
  venueId: string;
  overviewVersion: string;
  venueSuites: SuitesType;
  onClose: () => void;
};

export const VenueSuitesForm: React.FC<VenueSuitesFormPropsType> = ({
  venueId,
  overviewVersion,
  venueSuites,
  onClose,
}) => {
  const { t } = useTranslation('venueDetailsOverview');

  const { schema, defaultValues } = useVenueSuitesFormSchema(venueSuites);
  const { updateVenueSuites } = useUpdateVenueSuites({
    venueId,
    overviewVersion,
    onClose,
  });

  const methods = useForm<SuitesFormDataType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleSubmit = async (data: SuitesFormDataType) => {
    await updateVenueSuites(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Stack>
          <NumberField<SuitesFormDataType>
            fieldName="seats"
            label={t(`${TRANS_PATH}.SEATS_LABEL`)}
            allowDecimal={false}
          />
          <NumberField<SuitesFormDataType>
            fieldName="uplift"
            label={t(`${TRANS_PATH}.UPLIFT_LABEL`)}
            suffix="€"
          />
          <NumberField<SuitesFormDataType>
            fieldName="share"
            label={t(`${TRANS_PATH}.SHARE_LABEL`)}
            suffix="€"
          />
          <TextareaField<SuitesFormDataType>
            fieldName="notes"
            label={t(`${TRANS_PATH}.NOTES_LABEL`)}
          />
          <FormActions
            onCancel={onClose}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
